import React from 'react'
import styled from 'styled-components'

import { assignPrefix } from '../../../constants'
import { defaultTheme as theme } from '../../../theme'

const CardWrapper = styled.div`
  @media (min-width: ${theme.breakpoints.tabletVertical}px) {
    max-width: calc((100% / 3) - (32px /3));
  }
  max-width: 100%;
  width: 100%;
  background: ${theme.colors.gray[100]};
  border-radius: ${theme.radii.small};
  padding: ${theme.space[20]};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .creator {
    border-top: 1px solid ${theme.colors.gray[400]};
  }

  .creator a {
    margin-top: ${theme.space[16]};
    display: flex;
    color: ${theme.colors.gray[600]};
    text-wrap: nowrap;
  }

  .creator a .name {
    color: ${theme.colors.gray[900]};
    font-weight: bold;
    white-space: nowrap;
  }

  .details h5 {
    color: ${theme.colors.gray[900]};
    font-size: ${theme.fontSizes[16]};
    font-weight: bold;
    margin-top: ${theme.space[16]};
  }

  .details p {
    color: ${theme.colors.gray[600]};
    margin: ${theme.space[16]} 0;
    line-height: 1.5;
  }

  &:hover {
    background: ${theme.colors.gray[200]};
  }

  &:last-child {
    margin-right: 0;
  }

  .creator img {
    height: 24px;
    width: 24px;
    margin-right: ${theme.space[12]};
    border-radius: ${theme.space[24]};
  }

  @media (min-width: ${theme.breakpoints.tabletVertical}) and (max-width: 1024px) {
    .creator a .name {
      white-space: normal;
    }
  }
`

const ContributingWrapper = styled.div`
  box-shadow: 0px 20px 53px rgba(0, 0, 0, 0.07), 0px 4px 15.9779px rgba(0, 0, 0, 0.0393306),
    0px 2px 6.63642px rgba(0, 0, 0, 0.0238066), 0px 0px 2.40026px rgba(0, 0, 0, 0.0115598);
  border-radius: ${theme.space[8]};
  padding: ${theme.space[32]};
  background: ${theme.colors.white};
  margin: ${theme.space[32]} 0;

  header {
    display: flex;
    flex-direction: column;
    padding-bottom: ${theme.space[32]};
    margin-bottom: ${theme.space[32]};
    border-bottom: 1px solid rgba(204, 217, 223, 0.75);
  }

  .text p {
    color: ${theme.colors.gray[600]};
    margin: 1rem 0 2rem;
    line-height: 1.5;
  }

  header > * {
    flex: 1;
  }

  .icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: ${theme.colors.purple[100]};
    margin: 0 ${theme.space[24]} 0 0;
  }

  .call-to-actions {
    display: flex;
    align-content: center;
    align-items: auto;
    flex-direction: column;
  }

  .row {
    display: flex;
    justify-content: space-between;
  }

  .row a {
    flex: 1;
    cursor: pointer;
    width: auto;
    display: inline-flex;
    justify-content: space-between;
    user-select: none;
    font-weight: bold;
    color: ${theme.colors.gray[800]};
    padding: ${theme.space[8]} ${theme.space[20]};
    outline: none;
    background: ${theme.colors.gray[200]};
    border-radius: ${theme.radii.small};
    margin: 0 ${theme.space[20]} ${theme.space[16]};
    transition: background 140ms ease-out 0s, color 140ms ease-out 0s;
    align-items: center;
  }

  .row a:nth-child(2) {
    margin-right: 0;
  }

  .row a:hover {
    background: ${theme.colors.gray[300]};
  }

  .items {
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.space[16]};
  }

  @media (max-width: ${theme.breakpoints.tabletVertical}) {
    header {
      flex-direction: column;
    }

    .call-to-actions {
      margin-top: ${theme.space[32]};
    }

    .items {
      flex-direction: column;
    }
  }

  @media (max-width: ${theme.breakpoints.mobile}px) {
    padding: ${theme.space[16]};

    .call-to-actions .row {
      flex-direction: column;
    }

    .call-to-actions .row a {
      margin: 0 0 ${theme.space[20]};
    }
  }
`

const ArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15 10.8333V15.8333C15 16.2754 14.8244 16.6993 14.5118 17.0118C14.1993 17.3244 13.7754 17.5 13.3333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V6.66667C2.5 6.22464 2.67559 5.80072 2.98816 5.48816C3.30072 5.17559 3.72464 5 4.16667 5H9.16667"
      stroke={theme.colors.gray[800]}
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M12.5 2.5H17.5V7.5"
      stroke={theme.colors.gray[800]}
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M8.3335 11.6667L17.5002 2.5"
      stroke={theme.colors.gray[800]}
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
)

const GithubLogo = (
  <svg width="22" height="23" viewBox="0 0 22 23" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15 22.0001V18.1301C15.0375 17.6532 14.9731 17.1739 14.811 16.7239C14.6489 16.2738 14.3929 15.8635 14.06 15.5201C17.2 15.1701 20.5 13.9801 20.5 8.52006C20.4997 7.12389 19.9627 5.78126 19 4.77006C19.4559 3.54857 19.4236 2.19841 18.91 1.00006C18.91 1.00006 17.73 0.65006 15 2.48006C12.708 1.85888 10.292 1.85888 8 2.48006C5.27 0.65006 4.09 1.00006 4.09 1.00006C3.57638 2.19841 3.54414 3.54857 4 4.77006C3.03013 5.78876 2.49252 7.14352 2.5 8.55006C2.5 13.9701 5.8 15.1601 8.94 15.5501C8.611 15.89 8.35726 16.2955 8.19531 16.74C8.03335 17.1845 7.96681 17.6581 8 18.1301V22.0001M8 19.0001C3 20.5001 3 16.5001 1 16.0001L8 19.0001Z"
      stroke={theme.colors.black}
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
)

const YouTubeLogo = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.5401 6.42C22.4213 5.94541 22.1794 5.51057 21.8387 5.15941C21.4981 4.80824 21.0708 4.55318 20.6001 4.42C18.8801 4 12.0001 4 12.0001 4C12.0001 4 5.12008 4 3.40008 4.46C2.92933 4.59318 2.50206 4.84824 2.16143 5.19941C1.8208 5.55057 1.57887 5.98541 1.46008 6.46C1.1453 8.20556 0.991319 9.97631 1.00008 11.75C0.988863 13.537 1.14285 15.3213 1.46008 17.08C1.59104 17.5398 1.83839 17.9581 2.17823 18.2945C2.51806 18.6308 2.9389 18.8738 3.40008 19C5.12008 19.46 12.0001 19.46 12.0001 19.46C12.0001 19.46 18.8801 19.46 20.6001 19C21.0708 18.8668 21.4981 18.6118 21.8387 18.2606C22.1794 17.9094 22.4213 17.4746 22.5401 17C22.8524 15.2676 23.0064 13.5103 23.0001 11.75C23.0113 9.96295 22.8573 8.1787 22.5401 6.42V6.42Z"
      stroke={theme.colors.black}
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M9.75 15.02L15.5 11.75L9.75 8.47998V15.02Z"
      stroke={theme.colors.black}
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
)

const BookIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="23"
    viewBox="0 0 22 23"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
)

const CardItem = ({ href, icon, title, description, creator, creatorImage, creatorLink }: any) => (
  <CardWrapper>
    <a className="details" href={href}>
      <div>{icon}</div>
      <h5>{title}</h5>
      <p>{description}</p>
    </a>
    <div className="creator">
      <a href={creatorLink}>
        <img alt={creator} src={assignPrefix(creatorImage)} />
        by&nbsp;<span className="name">{creator}</span>
      </a>
    </div>
  </CardWrapper>
)

const CommunityContribution = () => (
  <ContributingWrapper>
    <div className="items">
      <CardItem
        href="https://paljs.com/"
        icon={GithubLogo}
        title="Pal.js"
        description="A set of tools to help you bootstrap your next GraphQL, React and Prisma project"
        creator="Ahmed Elywa"
        creatorImage="/blog/posts/contributors/ahmed.png"
        creatorLink="https://github.com/AhmedElywa"
      />
      <CardItem
        href="https://www.youtube.com/watch?v=z3rmZbOEBCM"
        icon={YouTubeLogo}
        title="Static Sites with Next.js 9.3 and Prisma"
        description="Lee Robinson walks us through his Prisma Day 2020 workshop on using getStaticProps with Prisma"
        creator="Lee Robinson"
        creatorImage="/blog/posts/contributors/lee.png"
        creatorLink="https://github.com/leerob"
      />
      <CardItem
        href="https://github.com/fivethree-team/nestjs-prisma-starter"
        icon={BookIcon}
        title="How to write tests for Prisma with Docker and Jest"
        description="A comprehensive tutorial teaching testing best practices with Prisma and Jest"
        creator="Eddy Nguyen"
        creatorImage="https://pbs.twimg.com/profile_images/1134695085751148544/dXRHDYyo_400x400.png"
        creatorLink="https://twitter.com/eddeee888"
      />
    </div>
  </ContributingWrapper>
)

export default CommunityContribution
